<template>
  <div class="tile">
    <a class="tile__link" :href="tileLink" @click="handleProductClick">
      <div class="tile__video">
        <IntersectionObserver v-slot="{ intersected }">
          <Video :intersected="intersected" :video="tileVideo" />
        </IntersectionObserver>
      </div>
    </a>
    <div class="tile__info">
      <a class="tile__link" :href="tileLink" @click="handleProductClick">
        <h3 class="tile__heading">{{ tileTitle }}</h3>
      </a>
      <Button
        v-if="product"
        :transparent="true"
        :light="true"
        size="small"
        @click="handleAddToBag"
      >
        add to bag |
        {{ $filters.toCurrency(price.amount, price.currencyCode) }}
      </Button>
    </div>
  </div>
</template>

<script>
import Button from "@/components/Button.vue";
import IntersectionObserver from "@/components/IntersectionObserver.vue";
import Video from "@/components/Video.vue";
import { unwrapNodes } from "@/helpers/graphql";
import { mapActions } from "vuex";
import { productClickEvent } from "@/helpers/events.js";

export default {
  name: "VideoTile",
  components: {
    Button,
    Video,
    IntersectionObserver,
  },
  props: {
    videoUrl: {
      type: String,
      default: null,
    },
    link: {
      type: String,
      default: null,
    },
    productId: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    product: {
      type: Object,
      default: () => {},
    },
    video: {
      type: Object,
      default: () => {},
    },
    position: {
      type: Number,
      default: null,
    },
    listName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      variantId: null,
    };
  },
  computed: {
    tileLink() {
      return this.product ? this.product.onlineStoreUrl : this.link;
    },
    tileTitle() {
      return this.product ? this.product.title : this.title;
    },
    tileVideo() {
      if (this.video) {
        const sources = this.video.sources;
        const previewImage = this.video.previewImage;

        return {
          sources,
          previewImage,
        };
      }

      if (this.videoUrl) {
        return {
          sources: [
            {
              url: this.videoUrl,
            },
          ],
        };
      }

      return null;
    },
    price() {
      return this.product.priceRange.minVariantPrice;
    },
    variants() {
      const variants = unwrapNodes(this.product?.variants.edges);
      return variants || null;
    },
    selectedVariant() {
      return this.variants.find((variant) => variant.id === this.variantId);
    },
  },
  methods: {
    ...mapActions("add-to-bag", ["openAddToBag"]),
    handleProductClick() {
      if (this.product) {
        productClickEvent({
          product: this.product,
          position: this.position,
          listName: this.listName,
        });
      }
    },
    handleAddToBag() {
      if (this.variants.length > 0) {
        this.openAddToBag(this.product);
      } else {
        console.error("Product has no variants", this.variants);
      }
      //Todo: Force add to bag if no variants
    },
    handleAddedToBag(variantId) {
      this.variantId = variantId;
      this.closeAddToBag();
    },
  },
};
</script>

<style lang="scss" scoped>
.tile {
  display: block;
  position: relative;

  &__info {
    font-size: 9px;
    position: absolute;
    bottom: 5px;
    left: 0;
    right: 0;
    padding: 0 15px 15px;
    color: $color-white;
    text-align: left;

    @include respond-above(md) {
      font-size: 10px;
      padding: 0 20px 20px;
    }

    :deep(.button) {
      padding: 0;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__video {
    &::after {
      content: "";
      background: linear-gradient(
        180deg,
        rgba(89, 89, 89, 0) 0%,
        rgba(1, 1, 1, 0.6) 100%
      );
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 75%;
    }
  }

  &__link {
    color: $color-white;
    display: block;
    height: 100%;
    line-height: 0;
    text-decoration: none;
  }

  &__heading {
    color: $color-white;
    font-size: 12px;
    font-weight: 700;
    line-height: normal;
    padding: 0;
    margin: 0 0 10px 0;
    text-transform: uppercase;

    @include respond-above(md) {
      font-size: 18px;
    }
  }

  &:deep(.picture__wrapper) {
    &:after {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      display: inline-block;
      background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.75) 0%,
        rgba(0, 0, 0, 0) 20%
      );
    }
  }
}
</style>
